import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { useRef, useState, type ReactElement } from 'react';

import { OnboardingHeader } from '../components/OnboardingHeader';
import { OnboardingProvider } from '../state/OnboardingProvider';
import { ONBOARDING_STEPS } from '../steps';
import type { OnboardingStepId } from '../types';
import { FooterSlotContext } from './footerSlotContext';

// Match the second path segment exactly so /onboarding/regions does not
// collide with a hypothetical /onboarding/regions-detail later.
function deriveCurrentStep(pathname: string): OnboardingStepId | undefined {
    const segment = pathname.split('/')[2];
    return ONBOARDING_STEPS.find((step) => step.id === segment)?.id;
}

// Retain the last real step so the stepper doesn't flash back to step one on exit.
function useCurrentOnboardingStep(): OnboardingStepId {
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const derivedStep = deriveCurrentStep(pathname);
    const lastStep = useRef<OnboardingStepId>(derivedStep ?? ONBOARDING_STEPS[0].id);
    if (derivedStep) {
        lastStep.current = derivedStep;
    }
    return lastStep.current;
}

export function OnboardingLayout(): ReactElement {
    const currentStep = useCurrentOnboardingStep();
    const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);

    return (
        <OnboardingProvider>
            <FooterSlotContext.Provider value={footerSlot ?? undefined}>
                <Stack sx={{ height: '100dvh', overflow: 'hidden' }}>
                    <OnboardingHeader currentStep={currentStep} />
                    <Stack
                        component="main"
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            bgcolor: { xs: 'background.paper', md: 'transparent' },
                        }}>
                        <Outlet />
                    </Stack>
                    <Box ref={setFooterSlot} sx={{ flexShrink: 0 }} />
                </Stack>
            </FooterSlotContext.Provider>
        </OnboardingProvider>
    );
}
